import { useEffect, useMemo, useState } from 'react';
import { useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { ago } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';
import { Page, Card, Row } from './Page.tsx';
import { GitHubMark } from '@/components/Pulses.tsx';
import { KindPill, kindOptions } from '@/components/TaskFacts.tsx';
import { Button, Checkbox, Field, Input, SectionLabel, Select, Textarea, Tip, useToast } from '@/components/ui.tsx';
import { cronExpressionError, type TaskKind, type TaskSchedule } from '@shared/domain.ts';
import type { ScheduleInput } from '@/lib/api.ts';

export function SettingsScreen() {
  const { data, project, isAdmin, config, refresh } = useRigg();
  const { notify } = useToast();
  // Settings is only ever reached with a single project active (see the
  // Sidebar's `projectOnly` pages), so this is the one row that matters here.
  const settings = project ? data.settings[project.id] : undefined;

  const [name, setName] = useState(project?.name ?? '');
  const [pat, setPat] = useState('');
  const [category, setCategory] = useState('');
  const [instructions, setInstructions] = useState(settings?.agent_instructions ?? '');
  const [designInstructions, setDesignInstructions] = useState(settings?.design_instructions ?? '');
  const [previewWorkflow, setPreviewWorkflow] = useState(settings?.preview_workflow ?? '');
  const [prodWorkflow, setProdWorkflow] = useState(settings?.prod_workflow ?? '');
  const [waiverCheck, setWaiverCheck] = useState(settings?.merge_waiver_check ?? '');
  const [versionUrl, setVersionUrl] = useState(settings?.prod_version_url ?? '');
  const [gateCheck, setGateCheck] = useState(settings?.deploy_gate_check ?? '');
  const [bypassInput, setBypassInput] = useState(settings?.deploy_gate_bypass_input ?? '');
  const [busy, setBusy] = useState(false);

  // Suggestions only — a check that has not run on any branch Rigg watches yet
  // is still a valid name, so this is a datalist rather than a closed list.
  const checkNames = useMemo(
    () => [...new Set(Object.values(data.ciRuns).map((run) => run.name))].sort((a, b) => a.localeCompare(b)),
    [data.ciRuns],
  );

  const categories = useMemo(
    () => Object.values(data.categories).sort((a, b) => a.label.localeCompare(b.label)),
    [data.categories],
  );

  // Only workflows GitHub has actually reported. Offering a free-text box here
  // invites a typo that silently matches nothing, and the failure is invisible:
  // no deploy status ever appears and nothing says why.
  // Asked of GitHub, not derived from runs Rigg has seen. A production deploy
  // is the rarest workflow a repository has, so deriving the list from observed
  // runs hides exactly the one worth naming until after the next release.
  const [workflows, setWorkflows] = useState<string[] | null>(null);
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    void api
      .workflows(project.id)
      .then((result) => {
        if (cancelled) return;
        setWorkflows(result.workflows);
        setWorkflowError(result.failed[0] ?? null);
      })
      .catch((error: Error) => !cancelled && setWorkflowError(error.message));
    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  // A saved value that GitHub no longer lists — renamed, or deleted — still
  // belongs in the list, or opening this page would silently discard it.
  const workflowOptions = useMemo(() => {
    const names = new Set(workflows ?? []);
    for (const saved of [settings?.preview_workflow, settings?.prod_workflow]) {
      if (saved) names.add(saved);
    }
    return [
      { value: '', label: 'Not set' },
      ...[...names].sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name })),
    ];
  }, [workflows, settings?.preview_workflow, settings?.prod_workflow]);

  const installations = Object.values(data.installations);
  const connected = installations.length > 0;
  const appConfigured = config?.github.app_configured ?? false;

  const guard = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      notify(label, { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="Settings" blurb={`Everything here applies to ${project?.name ?? 'this project'}.`}>
      {/* ── GitHub ─────────────────────────────────────────────────────── */}
      <SectionLabel className="mb-2.5">GitHub</SectionLabel>
      <Card>
        <div className="flex flex-wrap items-start gap-4">
          <GitHubMark className="mt-1 size-5 text-t2" />
          <div className="min-w-0 flex-1">
            {connected ? (
              <>
                <div className="text-[14px] font-semibold text-t1">
                  Connected to {installations.map((i) => i.account_login).join(', ')}
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-body">
                  Build status arrives by webhook, within a second of GitHub knowing it. Nothing is
                  polled, and comments are posted as the app rather than as somebody's account.
                </p>
              </>
            ) : appConfigured ? (
              <>
                <div className="text-[14px] font-semibold text-t1">App ready, not installed</div>
                <p className="mt-1 text-[13px] leading-relaxed text-body">
                  Install it on the repositories you want Rigg to watch. Until then, CI status has to
                  be polled with a personal access token.
                </p>
              </>
            ) : (
              <>
                <div className="text-[14px] font-semibold text-t1">No GitHub App yet</div>
                <p className="mt-1 text-[13px] leading-relaxed text-body">
                  A token can only be <em>polled</em>, so build status lags by minutes and burns
                  rate limit. An App has <code className="font-mono text-[12px]">workflow_run</code>{' '}
                  and <code className="font-mono text-[12px]">check_run</code> pushed to it the
                  moment they change — that is what makes “is GitHub working on this branch?”
                  answerable in real time. Creating one is two clicks; the manifest is filled in for
                  you.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {appConfigured && config?.github.install_url ? (
            <a href={config.github.install_url} target="_blank" rel="noreferrer">
              <Button tone="primary">{connected ? 'Manage installation' : 'Install the App'}</Button>
            </a>
          ) : (
            <a href={`${config?.functions_url ?? ''}/github-app/manifest`} target="_blank" rel="noreferrer">
              <Button tone="primary">Create the GitHub App</Button>
            </a>
          )}
          {connected ? <Button onClick={refresh}>Refresh</Button> : null}
        </div>

        {config ? (
          <div className="mt-4 grid gap-1.5 border-t border-line pt-3.5 text-[12px] text-mute">
            <div className="flex flex-wrap gap-2">
              <span className="w-24 shrink-0">Webhook</span>
              <code className="font-mono break-all text-t7">{config.github.webhook_url}</code>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="w-24 shrink-0">Callbacks</span>
              <code className="font-mono break-all text-t7">{config.functions_url}/agent</code>
            </div>
          </div>
        ) : null}
      </Card>

      <Card className="mt-3">
        <Field
          label="Personal access token (fallback)"
          hint={
            settings?.has_github_pat
              ? 'A token is stored. It is used only for repositories the App cannot see. Submit an empty field to remove it.'
              : 'Optional. Only needed for repositories outside the App — `gh auth token` prints one, and it needs `repo` scope for private repositories.'
          }
        >
          <Input
            type="password"
            value={pat}
            onChange={(event) => setPat(event.target.value)}
            placeholder={settings?.has_github_pat ? 'stored — type to replace' : 'ghp_…'}
            autoComplete="off"
            disabled={!isAdmin}
          />
        </Field>
        <Button
          className="mt-3"
          disabled={busy || !isAdmin}
          onClick={() =>
            guard('Could not save the token', async () => {
              await api.setGithubToken(project!.id, pat.trim() || null);
              setPat('');
              notify(pat.trim() ? 'Token saved' : 'Token removed');
            })
          }
        >
          {pat.trim() ? 'Save token' : 'Remove token'}
        </Button>
      </Card>

      {/* ── instructions ───────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Instructions for routines</SectionLabel>
      <Card>
        <Field
          label={`What every routine working on ${project?.name ?? 'this project'} should know`}
          hint="Added to the end of every brief, after the protocol — so where these and the protocol disagree, these win. Standing rules rather than task detail: which preview links to post, how branches are named, what to check before asking for review."
        >
          <Textarea
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            className="min-h-32"
            placeholder={'Post a Storybook link as well as the preview on any UI change.\nName branches feature/<ticket>.\nRun `npm run check` before reporting a preview.'}
            disabled={!isAdmin}
          />
        </Field>
        <Button
          className="mt-3"
          disabled={busy || !isAdmin || instructions === (settings?.agent_instructions ?? '')}
          onClick={() =>
            guard('Could not save the instructions', async () => {
              await api.setProjectSettings(project!.id, { agent_instructions: instructions.trim() || null });
              notify(instructions.trim() ? 'Instructions saved' : 'Instructions cleared');
            })
          }
        >
          Save instructions
        </Button>
      </Card>

      {/* ── design passes ──────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Instructions for design passes</SectionLabel>
      <Card>
        <Field
          label={`What a design pass on ${project?.name ?? 'this project'} means`}
          hint="Added to the end of every design brief. This is where the project-specific half lives — where the design system is, which tool to drive, and above all where a finished pass is committed so the routine finds it. The generic half (read the repo, brief the tool, export it verbatim, never write the design yourself) is in every brief already and does not need repeating."
        >
          <Textarea
            value={designInstructions}
            onChange={(event) => setDesignInstructions(event.target.value)}
            className="min-h-32"
            placeholder={
              'The design system lives in design/design-system/ and is mirrored to the Claude Design project of the same name.\n' +
              'Commit the export to design/design-system/work-batch/NNN-<slug>/ with a BRIEF.md beside it, and touch nothing else.\n' +
              'Take the next free number from work-batch/README.md and add a row to that ledger.'
            }
            disabled={!isAdmin}
          />
        </Field>
        <Button
          className="mt-3"
          disabled={busy || !isAdmin || designInstructions === (settings?.design_instructions ?? '')}
          onClick={() =>
            guard('Could not save the instructions', async () => {
              await api.setProjectSettings(project!.id, {
                design_instructions: designInstructions.trim() || null,
              });
              notify(designInstructions.trim() ? 'Instructions saved' : 'Instructions cleared');
            })
          }
        >
          Save instructions
        </Button>
      </Card>

      {/* ── deploys ────────────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Deploys</SectionLabel>
      <Card>
        <p className="text-[13px] leading-relaxed text-body">
          Rigg already sees every workflow run. Naming which one publishes a preview and which one
          goes to production turns those into an answer to the two questions worth asking: can I
          look at it yet, and is it out?
        </p>
        {workflowError ? (
          <p className="mt-3 text-[13px] text-warnx">
            Could not read the workflows from GitHub — {workflowError}
          </p>
        ) : workflows === null ? (
          <p className="mt-3 text-[13px] text-mute">Reading the workflows from GitHub…</p>
        ) : null}
        <div className="mt-3 flex flex-col gap-3">
          <Field label="Preview" hint="Run on a task's branch. Its state becomes the task's preview status.">
            <Select
              value={previewWorkflow}
              onChange={setPreviewWorkflow}
              options={workflowOptions}
            />
          </Field>
          <Field
            label="Production"
            hint="Whatever puts it live. A task counts as in production once this succeeds after the task merged."
          >
            <Select value={prodWorkflow} onChange={setProdWorkflow} options={workflowOptions} />
          </Field>
          <Field
            label="Live version URL"
            hint="Answers with the commit production runs, e.g. https://api.brigg.app/version. The Deploy dialog lists what has merged since."
          >
            <Input
              value={versionUrl}
              onChange={(event) => setVersionUrl(event.target.value)}
              placeholder="https://…/version"
              disabled={!isAdmin}
            />
          </Field>
          <Field
            label="Verified check"
            hint="A commit status or check run that marks a commit fit for production. A deploy ships the newest commit carrying it. Empty: a deploy ships everything merged."
          >
            <Input
              value={gateCheck}
              onChange={(event) => setGateCheck(event.target.value)}
              list="rigg-check-names"
              placeholder="full-suite"
              disabled={!isAdmin}
            />
          </Field>
          <Field
            label="Incident input"
            hint="The production workflow's true/false input that ships everything merged, past the verified check. The Deploy dialog shows it as the Incident switch."
          >
            <Input
              value={bypassInput}
              onChange={(event) => setBypassInput(event.target.value)}
              placeholder="skip_batch_gate"
              disabled={!isAdmin}
            />
          </Field>
          <div>
            <Button
              disabled={
                busy ||
                !isAdmin ||
                (previewWorkflow === (settings?.preview_workflow ?? '') &&
                  prodWorkflow === (settings?.prod_workflow ?? '') &&
                  versionUrl.trim() === (settings?.prod_version_url ?? '') &&
                  gateCheck.trim() === (settings?.deploy_gate_check ?? '') &&
                  bypassInput.trim() === (settings?.deploy_gate_bypass_input ?? ''))
              }
              onClick={() =>
                guard('Could not save the workflows', async () => {
                  await api.setProjectSettings(project!.id, {
                    preview_workflow: previewWorkflow.trim() || null,
                    prod_workflow: prodWorkflow.trim() || null,
                    prod_version_url: versionUrl.trim() || null,
                    deploy_gate_check: gateCheck.trim() || null,
                    deploy_gate_bypass_input: bypassInput.trim() || null,
                  });
                  notify('Deploy workflows saved');
                })
              }
            >
              Save workflows
            </Button>
          </div>
        </div>
      </Card>

      {/* ── merging ────────────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Merging</SectionLabel>
      <Card>
        <p className="text-[13px] leading-relaxed text-body">
          Rigg lands a pull request once it is approved, clean, green and reviewed at its head. Name a
          check here and a <em>successful</em> newest run of it at the head stands in for the approval
          — only the approval. Clean, green, <code className="font-mono text-[12px]">reviewed_sha</code>{' '}
          and the default branch's own health still have to hold, a cancelled or missing run grants
          nothing, and sending the work back withdraws it. Only a GitHub Actions run counts, and a pull
          request that changes <code className="font-mono text-[12px]">.github/</code> is never
          waived — it could otherwise write its own verdict. Leave it empty and every merge waits for
          a person.
        </p>
        <div className="mt-3 flex flex-col gap-3">
          <Field
            label="Approval waiver check"
            hint="The check-run name exactly as GitHub shows it, case included — for example “Auto-merge eligible”."
          >
            <Input
              value={waiverCheck}
              onChange={(event) => setWaiverCheck(event.target.value)}
              placeholder="Not set — every merge needs an approval"
              list="rigg-check-names"
              disabled={!isAdmin}
              className="max-w-md"
            />
          </Field>
          <datalist id="rigg-check-names">
            {checkNames.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <div>
            <Button
              disabled={busy || !isAdmin || waiverCheck.trim() === (settings?.merge_waiver_check ?? '')}
              onClick={() =>
                guard('Could not save the waiver', async () => {
                  await api.setProjectSettings(project!.id, { merge_waiver_check: waiverCheck.trim() || null });
                  notify(waiverCheck.trim() ? 'Waiver check saved' : 'Waiver removed');
                })
              }
            >
              Save
            </Button>
          </div>
        </div>
      </Card>

      {/* ── schedules ──────────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Scheduled tasks</SectionLabel>
      {project ? <Schedules projectId={project.id} isAdmin={isAdmin} /> : null}

      {/* ── categories ─────────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Categories</SectionLabel>
      <Card>
        <p className="text-[13px] leading-relaxed text-body">
          Categories are dynamic. A routine opening a task can name one that does not exist yet —{' '}
          <em>Maintenance</em>, <em>Security</em>, <em>Flaky tests</em> — and it is created on the
          spot. These are the ones this project has seen.
        </p>
        <div className="mt-3">
          {categories.length === 0 ? (
            <p className="text-[13px] text-mute">None yet.</p>
          ) : (
            categories.map((entry) => (
              <Row
                key={entry.id}
                title={
                  <span className="flex items-center gap-2">
                    <span
                      className="size-2.5 rounded-full"
                      style={{ background: entry.color ?? 'var(--color-mute)' }}
                      aria-hidden
                    />
                    {entry.label}
                  </span>
                }
                subtitle={
                  entry.created_by_agent_id
                    ? `invented by ${data.agents[entry.created_by_agent_id]?.name ?? 'a routine'}`
                    : undefined
                }
                meta={<span className="font-mono text-[11.5px]">{entry.slug}</span>}
                actions={
                  isAdmin ? (
                    <Button
                      size="sm"
                      tone="danger"
                      onClick={() => void api.removeCategory(entry.id).catch(() => undefined)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            ))
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <Input
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder="Add a category"
            className="max-w-xs"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && category.trim()) {
                void guard('Could not add it', async () => {
                  await api.addCategory(project!.id, category.trim());
                  setCategory('');
                });
              }
            }}
          />
          <Button
            disabled={busy || !category.trim()}
            onClick={() =>
              guard('Could not add it', async () => {
                await api.addCategory(project!.id, category.trim());
                setCategory('');
              })
            }
          >
            Add
          </Button>
        </div>
      </Card>

      {/* ── project ────────────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Project</SectionLabel>
      <Card>
        <Field label="Name">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={!isAdmin}
            className="max-w-sm"
          />
        </Field>
        <Tip content={isAdmin ? '' : 'Only an admin can rename a project'}>
          <span className="mt-3 inline-block">
            <Button
              disabled={busy || !isAdmin || !name.trim() || name === project?.name}
              onClick={() =>
                guard('Could not rename it', async () => {
                  await api.renameProject(project!.id, name.trim());
                  refresh();
                })
              }
            >
              Rename
            </Button>
          </span>
        </Tip>
      </Card>

      {/* ── developer ──────────────────────────────────────────────────── */}
      <SectionLabel className="mt-8 mb-2.5">Browser extension</SectionLabel>
      <ExtensionSettings />

      <SectionLabel className="mt-8 mb-2.5">API keys</SectionLabel>
      <ApiKeys />
    </Page>
  );
}

/**
 * The Chrome extension that turns pointing at a live page into a Rigg task —
 * see apps/extension/README.md. It authenticates with a personal API key like
 * any other script, but getting that key into the extension should not mean
 * copy-pasting one: `externally_connectable` (declared in the extension's
 * manifest) lets this page hand it over directly, the moment it is minted.
 *
 * Falls back to showing the key for a manual paste — into the panel's own
 * "connect by hand" form — whenever the extension cannot be reached: not
 * installed, a browser that does not support the messaging, or a Rigg
 * deployed at an origin the extension was not built to recognise.
 */
function ExtensionSettings() {
  const { project, config } = useRigg();
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [manualKey, setManualKey] = useState<string | null>(null);

  const extensionId = import.meta.env.VITE_RIGG_EXTENSION_ID;

  const connect = async () => {
    if (!project || !config) return;
    setBusy(true);
    setStatus(null);
    setManualKey(null);
    try {
      const minted = await api.mintKey('Browser extension');
      const runtime = (
        window as unknown as {
          chrome?: {
            runtime?: {
              sendMessage?: (extensionId: string, message: unknown, callback: (response: unknown) => void) => void;
              lastError?: unknown;
            };
          };
        }
      ).chrome?.runtime;

      if (!extensionId || !runtime?.sendMessage) {
        setManualKey(minted.key);
        setStatus({ tone: 'bad', text: 'Extension not detected — copy the key below into the panel’s “connect by hand” form.' });
        return;
      }

      await new Promise<void>((resolve) => {
        runtime.sendMessage!(
          extensionId,
          {
            type: 'rigg:connect',
            token: minted.key,
            apiBase: `${config.functions_url}/api`,
            appUrl: config.app_url,
            projectId: project.id,
            projectName: project.name,
          },
          (response) => {
            const ok = !runtime.lastError && (response as { ok?: boolean } | undefined)?.ok;
            if (ok) {
              setStatus({ tone: 'ok', text: 'Connected. Open the extension’s side panel to start pointing at things.' });
            } else {
              setManualKey(minted.key);
              setStatus({ tone: 'bad', text: 'Could not reach the extension — is it installed? Copy the key below into the panel instead.' });
            }
            resolve();
          },
        );
      });
    } catch (error) {
      notify('Could not connect the extension', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify('Key copied');
    } catch {
      notify('Could not copy');
    }
  };

  return (
    <Card>
      <p className="text-[13px] leading-relaxed text-body">
        Point at something in a live page — an element, a region, or text to edit — describe the
        change, and it lands in a workbatch you send straight to this project as a task, screenshots
        and all. Figma-style annotation over whatever app you already have open.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a
          href="/rigg-extension.zip"
          download
          className="rg-focus inline-flex items-center justify-center gap-1.5 rounded-full bg-accent px-4 py-2 text-[13px] font-[650] text-ink transition-[filter] duration-100 hover:brightness-110 active:brightness-95"
        >
          Download extension
        </a>
        <Button disabled={busy || !project} onClick={() => void connect()}>
          Connect extension
        </Button>
      </div>

      {status ? (
        <p className={`mt-3 text-[13px] ${status.tone === 'ok' ? 'text-accent' : 'text-mute'}`}>{status.text}</p>
      ) : null}

      {manualKey ? (
        <div className="mt-3 flex flex-col gap-3 rounded-xl border border-accent/30 bg-accent/6 p-4">
          <div className="text-[13px] font-[650] text-accent">Copy this now — it is not shown again.</div>
          <code className="rounded-lg border border-inputbd bg-input px-3 py-2 font-mono text-[12px] break-all text-t1">
            {manualKey}
          </code>
          <Button size="sm" tone="primary" className="self-start" onClick={() => void copy(manualKey)}>
            Copy key
          </Button>
        </div>
      ) : null}

      <p className="mt-4 text-[12px] leading-relaxed text-mute">
        Download and load it unpacked (chrome://extensions, Developer mode on), then come back here
        and click Connect extension — it recognises this browser automatically. The extension gets its
        own key, worth the same as your own sign-in for this project; revoke it any time from API keys
        below.
      </p>
    </Card>
  );
}

/**
 * Personal API keys.
 *
 * Not project-scoped, unlike everything above on this page — a key is worth
 * whatever the person who minted it is worth, the same as their session, so
 * it follows them across every project they belong to rather than living
 * under one. See `POST /api/keys` and the API docs it links to.
 */
function ApiKeys() {
  const { notify } = useToast();

  const [keys, setKeys] = useState<
    { id: string; hint: string; label: string | null; created_at: string; last_used_at: string | null; revoked_at: string | null }[]
  >([]);
  const [label, setLabel] = useState('');
  const [minted, setMinted] = useState<{ key: string; docs_url: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .keys()
      .then((result) => setKeys(result.keys))
      .catch(() => setKeys([]));
  };

  useEffect(load, []);

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify(`${what} copied`);
    } catch {
      notify('Could not copy');
    }
  };

  return (
    <Card>
      <p className="text-[13px] leading-relaxed text-body">
        A key does everything your own sign-in does — submit a task, approve one, manage a
        project — for every project you belong to, from a script rather than a browser. See the{' '}
        <button
          onClick={() => navigate('/api-docs')}
          className="rg-focus font-semibold text-accent underline-offset-2 hover:underline"
        >
          API docs
        </button>{' '}
        for the endpoints.
      </p>

      {minted ? (
        <div className="mt-3 flex flex-col gap-3 rounded-xl border border-accent/30 bg-accent/6 p-4">
          <div className="text-[13px] font-[650] text-accent">
            Copy this now — it is not shown again.
          </div>
          <code className="rounded-lg border border-inputbd bg-input px-3 py-2 font-mono text-[12px] break-all text-t1">
            {minted.key}
          </code>
          <Button size="sm" tone="primary" className="self-start" onClick={() => void copy(minted.key, 'Key')}>
            Copy key
          </Button>
        </div>
      ) : null}

      <div className="mt-4">
        {keys.length === 0 ? (
          <p className="text-[13px] text-mute">No API keys yet.</p>
        ) : (
          keys.map((key) => (
            <Row
              key={key.id}
              title={<span className="font-mono text-[13px]">{key.hint}</span>}
              subtitle={key.label ?? undefined}
              meta={
                key.revoked_at
                  ? 'revoked'
                  : key.last_used_at
                    ? `last used ${ago(key.last_used_at)}`
                    : 'never used'
              }
              actions={
                key.revoked_at ? null : (
                  <Button
                    size="sm"
                    tone="danger"
                    onClick={async () => {
                      await api.revokeKey(key.id).catch(() => undefined);
                      load();
                    }}
                  >
                    Revoke
                  </Button>
                )
              }
            />
          ))
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <Input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="What is this key for? (optional)"
          className="max-w-xs"
        />
        <Button
          tone="primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const result = await api.mintKey(label.trim() || undefined);
              setMinted({ key: result.key, docs_url: result.docs_url });
              setLabel('');
              load();
            } catch (error) {
              notify('Could not mint a key', { body: (error as Error).message, tone: 'bad' });
            } finally {
              setBusy(false);
            }
          }}
        >
          Mint a new key
        </Button>
      </div>
    </Card>
  );
}

const EMPTY_SCHEDULE: ScheduleInput = {
  cron: '',
  title: '',
  prompt: '',
  category: null,
  kind: null,
  auto_merge: false,
  smart: false,
  agent_id: null,
  fire_and_forget: false,
};

/** The picker's value for "no routine named" — Radix refuses an empty one. */
const ROTATION = 'auto';

/**
 * Tasks Rigg opens on a timer.
 *
 * Each one is an ordinary task when it fires — the same call the composer
 * makes, dispatched to the next routine in rotation unless it names one — so
 * what is edited here is only what would have been typed, plus when. Read
 * through the API rather than Realtime: this is the only screen that shows
 * them, and what each last did changes once a week, not once a second.
 */
function Schedules({ projectId, isAdmin }: { projectId: string; isAdmin: boolean }) {
  const { data } = useRigg();
  const { notify } = useToast();
  const [schedules, setSchedules] = useState<TaskSchedule[] | null>(null);
  // `null` = no form open; `'new'` = adding; otherwise the id being edited.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<ScheduleInput>(EMPTY_SCHEDULE);
  const [busy, setBusy] = useState(false);

  // Paused ones included: pinning is how a routine kept out of the rotation
  // still gets the one job it holds the credentials for.
  const routines = useMemo(
    () =>
      Object.values(data.agents)
        .filter((agent) => agent.project_id === projectId && agent.kind === 'routine')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data.agents, projectId],
  );

  const load = () => {
    api
      .schedules(projectId)
      .then((result) => setSchedules(result.schedules))
      .catch((error: Error) => {
        setSchedules([]);
        notify('Could not read the schedules', { body: error.message, tone: 'bad' });
      });
  };
  useEffect(load, [projectId]);

  const open = (schedule: TaskSchedule | null) => {
    setEditing(schedule?.id ?? 'new');
    setDraft(
      schedule
        ? {
            cron: schedule.cron,
            title: schedule.title,
            prompt: schedule.prompt,
            category: schedule.category,
            kind: schedule.kind,
            auto_merge: schedule.auto_merge,
            smart: schedule.smart,
            agent_id: schedule.agent_id,
            fire_and_forget: schedule.fire_and_forget,
          }
        : EMPTY_SCHEDULE,
    );
  };

  const cronError = draft.cron.trim() ? cronExpressionError(draft.cron) : null;
  const ready = draft.title.trim() && draft.prompt.trim() && draft.cron.trim() && !cronError;

  const save = async () => {
    setBusy(true);
    try {
      const input = { ...draft, category: draft.category?.trim() || null };
      if (editing === 'new') await api.addSchedule({ ...input, project_id: projectId });
      else if (editing) await api.updateSchedule(editing, input);
      notify('Schedule saved');
      setEditing(null);
      load();
    } catch (error) {
      notify('Could not save the schedule', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (schedule: TaskSchedule) => {
    setBusy(true);
    try {
      await api.removeSchedule(schedule.id);
      notify(`Removed “${schedule.title}”`);
      load();
    } catch (error) {
      notify('Could not remove it', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <p className="text-[13px] leading-relaxed text-body">
        A schedule opens an ordinary task on a cron expression — the same task the composer would
        have raised, handed to the next routine in rotation, or to the one routine it names — unless
        it is fire and forget, when the routine gets the prompt and the board stays clear. Times are{' '}
        <strong className="font-semibold">UTC</strong>, like every other job Rigg runs:{' '}
        <code className="font-mono text-[12px]">0 6 * * 1</code> is Monday 06:00 UTC, which is 07:00
        in Oslo in winter and 08:00 in summer.
      </p>

      <div className="mt-3">
        {schedules === null ? (
          <p className="text-[13px] text-mute">Reading the schedules…</p>
        ) : schedules.length === 0 ? (
          <p className="text-[13px] text-mute">None yet.</p>
        ) : (
          schedules.map((schedule) => {
            const last = schedule.last_task_id ? data.tasks[schedule.last_task_id] : null;
            return (
              <Row
                key={schedule.id}
                title={schedule.title}
                subtitle={
                  schedule.last_error
                    ? `last run ${schedule.last_fired_at ? new Date(schedule.last_fired_at).toLocaleString() : ''} failed — ${schedule.last_error}`
                    : schedule.last_fired_at
                      ? `last opened ${last ? `#${last.ref}` : 'a task'} on ${new Date(schedule.last_fired_at).toLocaleString()}`
                      : 'has not fired yet'
                }
                meta={
                  <span className="flex items-center gap-2 text-[12px]">
                    <code className="font-mono text-[12px] text-t7">{schedule.cron}</code>
                    <KindPill kind={schedule.kind} variant="dark" />
                    {schedule.category ? <span>{schedule.category}</span> : null}
                    {schedule.auto_merge ? <span className="text-violetx">Auto-merge</span> : null}
                    {schedule.fire_and_forget ? <span className="text-mute">Fire and forget</span> : null}
                    {schedule.smart && !schedule.agent_id ? <span className="text-violetx">Smart</span> : null}
                    {schedule.agent_id ? (
                      <span className="text-violetx">
                        → {data.agents[schedule.agent_id]?.name ?? 'a routine'}
                      </span>
                    ) : null}
                  </span>
                }
                actions={
                  isAdmin ? (
                    <span className="flex gap-1.5">
                      <Button size="sm" disabled={busy} onClick={() => open(schedule)}>
                        Edit
                      </Button>
                      <Button size="sm" tone="danger" disabled={busy} onClick={() => void remove(schedule)}>
                        Remove
                      </Button>
                    </span>
                  ) : null
                }
              />
            );
          })
        )}
      </div>

      {editing ? (
        <div className="mt-4 flex flex-col gap-3 border-t border-line pt-4">
          <Field label="Title" hint="What the task is called on the board.">
            <Input
              value={draft.title}
              onChange={(event) => setDraft((d) => ({ ...d, title: event.target.value }))}
              placeholder="Weekly stale-PR sweep"
            />
          </Field>
          <Field
            label="When (cron, UTC)"
            hint={cronError ?? 'minute hour day-of-month month day-of-week — numbers, *, lists, ranges and /steps.'}
          >
            <Input
              value={draft.cron}
              onChange={(event) => setDraft((d) => ({ ...d, cron: event.target.value }))}
              placeholder="0 6 * * 1"
              className="max-w-xs font-mono"
            />
          </Field>
          <Field label="Prompt" hint="Handed to the routine exactly as a person typing it into the composer would.">
            <Textarea
              value={draft.prompt}
              onChange={(event) => setDraft((d) => ({ ...d, prompt: event.target.value }))}
              className="min-h-24"
              placeholder="Use the `sweep-stale-prs` skill to sweep this repo's open pull requests."
            />
          </Field>
          <Field label="Category (optional)" hint="Free text — created if the project has not seen it.">
            <Input
              value={draft.category ?? ''}
              onChange={(event) => setDraft((d) => ({ ...d, category: event.target.value }))}
              placeholder="Maintenance"
              list="rigg-category-names"
              className="max-w-xs"
            />
          </Field>
          <Field label="Kind (optional)" hint="Every task this schedule opens is this kind.">
            <Select
              value={draft.kind ?? 'none'}
              onChange={(value) => setDraft((d) => ({ ...d, kind: value === 'none' ? null : (value as TaskKind) }))}
              options={kindOptions('No kind')}
              aria-label="Kind"
            />
          </Field>
          <datalist id="rigg-category-names">
            {Object.values(data.categories).map((c) => (
              <option key={c.id} value={c.label} />
            ))}
          </datalist>
          <Field
            label="Routine"
            hint="Name one when only it can do the job — say, it holds credentials the others do not. A named routine gets every firing, paused or not, and never falls back to the rotation. Fire and forget needs one."
          >
            <Select
              value={draft.agent_id ?? ROTATION}
              onChange={(value) =>
                setDraft((d) =>
                  value === ROTATION
                    ? // Fire and forget has no queue to wait in for the rotation.
                      { ...d, agent_id: null, fire_and_forget: false }
                    : { ...d, agent_id: value },
                )
              }
              options={[
                { value: ROTATION, label: 'Rotation (auto-pick)' },
                ...routines.map((agent) => ({
                  value: agent.id,
                  label: agent.name,
                  hint: agent.paused ? 'paused' : undefined,
                })),
              ]}
              className="max-w-xs"
            />
          </Field>
          <div className="flex flex-col gap-2">
            {/*
              As on an intake: mutually exclusive with auto-merge — there is no
              callback for an approval to apply to — so checking it clears that
              one. Only offered once a routine is named; the server refuses it
              on the rotation (see the 20261004160000 migration).
            */}
            {draft.agent_id ? (
              <div className="flex flex-col gap-1.5">
                <Checkbox
                  id="schedule-fire-and-forget"
                  checked={draft.fire_and_forget}
                  onChange={(fire_and_forget) =>
                    setDraft((d) => ({ ...d, fire_and_forget, auto_merge: fire_and_forget ? false : d.auto_merge }))
                  }
                  label="No callback — fire and forget"
                />
                <span className="text-[11.5px] leading-relaxed text-faint">
                  Every firing goes straight to the routine with nothing to approve, poll or report
                  back to. Rigg still keeps a task for the record, but it stays off the board — filter
                  by source to find it. Use this for recurring work whose output lands somewhere else.
                </span>
              </div>
            ) : null}
            {draft.fire_and_forget ? null : (
              <Checkbox
                id="schedule-auto-merge"
                checked={draft.auto_merge}
                onChange={(auto_merge) => setDraft((d) => ({ ...d, auto_merge }))}
                label="Approved from the outset (auto-merge)"
              />
            )}
            {draft.agent_id ? null : (
              <Checkbox
                id="schedule-smart"
                checked={draft.smart}
                onChange={(smart) => setDraft((d) => ({ ...d, smart }))}
                label="Pick a smart routine instead of a normal one"
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button tone="primary" disabled={busy || !ready} onClick={() => void save()}>
              {editing === 'new' ? 'Add schedule' : 'Save schedule'}
            </Button>
            <Button disabled={busy} onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : isAdmin ? (
        <Button className="mt-3" onClick={() => open(null)}>
          Add a schedule
        </Button>
      ) : null}
    </Card>
  );
}
